import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from 'config';
import type {
  WorkExperience,
  EducationDetails,
  JobProfileCertificationBase,
  JobProfileProject,
  SocialLinksBody,
  UpsertCandidateDetailsBody,
} from 'src/openapi';
import type {
  IAward,
  ICertification,
  ILanguage,
  IPublication,
  IVolunteerExperience,
  IPositionOfResponsibility,
  IInterest,
} from 'src/components/candidateProfile/types';
import { resumeBlobFromResponse } from 'src/helpers/resumeFile';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import { enqueueSnackbar } from 'notistack';
import type { RootState } from '..';
import type { ProfileCompletionItemKey } from '../types/candidateProfile';

export const deleteSkill = createAsyncThunk(
  'job/delete-user-skill',
  async ({ id }: any, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteUserSkill({ skillId: id });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchResume = createAsyncThunk(
  'users/self/resume',
  async (_, { rejectWithValue, fulfillWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const res = await axios.get(`${config.API_URL}/users/self/resume`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${state.auth.token}`,
        },
      });
      const blob = resumeBlobFromResponse(res);
      const url = window.URL.createObjectURL(blob);
      return fulfillWithValue({ data_url: url, content_type: blob.type });
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateEducation = createAsyncThunk(
  'put/update-education',
  async (
    { id, payload }: { id: string; payload: EducationDetails },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiChangeEducationDetails({
        educationId: id,
        educationDetails: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postEducation = createAsyncThunk(
  'post/add-new-education',
  async ({ payload }: { payload: EducationDetails }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiEducationDetails({ educationDetails: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteEducation = createAsyncThunk(
  `delete/education`,
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteEducationDetails({ educationId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postExperience = createAsyncThunk(
  'post/add-experience',
  async ({ payload }: { payload: WorkExperience }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiWorkDetails({ workExperience: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateExperience = createAsyncThunk(
  'put/update-experience',
  async (
    { id, payload }: { id: string; payload: WorkExperience },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiChangeWorkDetails({ workId: id, workExperience: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteExperience = createAsyncThunk(
  `user/delete-experience`,
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteWorkDetails({ workId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postProject = createAsyncThunk(
  'post/add-project',
  async ({ payload }: { payload: JobProfileProject }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiProject({ jobProfileProject: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateProject = createAsyncThunk(
  'put/update-project',
  async (
    { id, payload }: { id: string; payload: JobProfileProject },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiChangeProject({ projectId: id, jobProfileProject: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteProject = createAsyncThunk(
  `delete/project`,
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteProject({ projectId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadProjectImage = createAsyncThunk(
  'files/upload-project-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadProjectImage({ file });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postCertification = createAsyncThunk(
  'post/add-certification',
  async ({ payload }: { payload: ICertification }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAddCertification({
        jobProfileCertificationBase: payload as JobProfileCertificationBase,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateCertification = createAsyncThunk(
  'put/update-certification',
  async (
    { id, payload }: { id: string; payload: ICertification },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateCertification({
        certId: id,
        jobProfileCertificationBase: payload as JobProfileCertificationBase,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteCertification = createAsyncThunk(
  'delete/certification',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteCertification({ certId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

// Certificate scans go to their own bucket prefix and are capped at 1MB by the
// backend schema; the picker rejects oversized files before we get here so the
// candidate sees the reason rather than a 422.
export const uploadCertificationImage = createAsyncThunk(
  'files/upload-certification-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadCertificationImage({ file });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postAward = createAsyncThunk(
  'post/add-award',
  async ({ payload }: { payload: IAward }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAddAward({ jobProfileAwardBase: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateAward = createAsyncThunk(
  'put/update-award',
  async (
    { id, payload }: { id: string; payload: IAward },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateAward({ awardId: id, jobProfileAwardBase: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteAward = createAsyncThunk(
  'delete/award',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteAward({ awardId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postLanguage = createAsyncThunk(
  'post/add-language',
  async ({ payload }: { payload: ILanguage }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAddLanguage({ jobProfileLanguageBase: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateLanguage = createAsyncThunk(
  'put/update-language',
  async (
    { id, payload }: { id: string; payload: ILanguage },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateLanguage({
        languageId: id,
        jobProfileLanguageBase: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteLanguage = createAsyncThunk(
  'delete/language',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteLanguage({ languageId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postPublication = createAsyncThunk(
  'post/add-publication',
  async ({ payload }: { payload: IPublication }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAddPublication({ jobProfilePublicationBase: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePublication = createAsyncThunk(
  'put/update-publication',
  async (
    { id, payload }: { id: string; payload: IPublication },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdatePublication({
        publicationId: id,
        jobProfilePublicationBase: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deletePublication = createAsyncThunk(
  'delete/publication',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeletePublication({ publicationId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postVolunteerExperience = createAsyncThunk(
  'post/add-volunteer-experience',
  async (
    { payload }: { payload: IVolunteerExperience },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAddVolunteerExperience({
        jobProfileVolunteerExperienceBase: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateVolunteerExperience = createAsyncThunk(
  'put/update-volunteer-experience',
  async (
    { id, payload }: { id: string; payload: IVolunteerExperience },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateVolunteerExperience({
        volunteerId: id,
        jobProfileVolunteerExperienceBase: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteVolunteerExperience = createAsyncThunk(
  'delete/volunteer-experience',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteVolunteerExperience({ volunteerId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

// One thunk shape for the two sections served by hand-written client calls.
const withSnackbarOnError =
  <A, R>(call: (arg: A) => Promise<R>) =>
  async (arg: A, { rejectWithValue }: { rejectWithValue: (v: any) => any }) => {
    try {
      return await call(arg);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  };

export const autofillProfileFromResume = createAsyncThunk(
  'post/autofill-profile-from-resume',
  withSnackbarOnError(({ resumeId }: { resumeId: string }) =>
    apiClient.autofillProfileFromResume(resumeId),
  ),
);

export const postPositionOfResponsibility = createAsyncThunk(
  'post/add-position-of-responsibility',
  withSnackbarOnError(({ payload }: { payload: IPositionOfResponsibility }) =>
    apiClient.addPositionOfResponsibility(payload),
  ),
);

export const updatePositionOfResponsibility = createAsyncThunk(
  'put/update-position-of-responsibility',
  withSnackbarOnError(
    ({ id, payload }: { id: string; payload: IPositionOfResponsibility }) =>
      apiClient.updatePositionOfResponsibility(id, payload),
  ),
);

export const deletePositionOfResponsibility = createAsyncThunk(
  'delete/position-of-responsibility',
  withSnackbarOnError(({ id }: { id: string }) =>
    apiClient.deletePositionOfResponsibility(id),
  ),
);

export const postInterest = createAsyncThunk(
  'post/add-interest',
  withSnackbarOnError(({ payload }: { payload: IInterest }) =>
    apiClient.addInterest(payload),
  ),
);

export const updateInterest = createAsyncThunk(
  'put/update-interest',
  withSnackbarOnError(({ id, payload }: { id: string; payload: IInterest }) =>
    apiClient.updateInterest(id, payload),
  ),
);

export const deleteInterest = createAsyncThunk(
  'delete/interest',
  withSnackbarOnError(({ id }: { id: string }) => apiClient.deleteInterest(id)),
);

// Runs in the background after a profile save, so it stays quiet on failure:
// the checklist keeps its last value.
export const fetchProfileCompletion = createAsyncThunk(
  'user/profile-completion',
  async (_: void, { rejectWithValue }) => {
    try {
      return await apiClient.getProfileCompletion();
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const setProfileItemSkipped = createAsyncThunk(
  'put/profile-completion-skip',
  withSnackbarOnError(
    (body: { item: ProfileCompletionItemKey; skipped: boolean }) =>
      apiClient.setProfileItemSkipped(body),
  ),
);

export const updatePreference = createAsyncThunk(
  'user/candidate-meta-details',
  async (payload: UpsertCandidateDetailsBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpsertCandidateMetaDetails({
        upsertCandidateDetailsBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postSocialLinks = createAsyncThunk(
  'post/social-links',
  async (links: SocialLinksBody[], { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpsertSocialLinks({ socialLinksBody: links });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteSocialLink = createAsyncThunk(
  'delete/social-links',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteSocialLink({ socialLinkId: id });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchCandidateDetails = createAsyncThunk(
  'user/fetchDetails',
  async (_: { force?: boolean } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiResumeDetails();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
  {
    // The sidebar and the page under it both ask on a cold load; one request
    // answers both. A refetch after a write passes `force`, since a read
    // already in flight may predate the write.
    condition: (params, { getState }) =>
      !!params?.force ||
      !(getState() as RootState).candidateProfile.isFetchingCandidateDetails,
  },
);

export const uploadResume = createAsyncThunk(
  'files/upload-resume',
  async (
    {
      file,
      setAsDefault = false,
      fastParse,
    }: { file: any; setAsDefault?: boolean; fastParse?: boolean },
    { rejectWithValue },
  ) => {
    // A 0-byte file is what a failed download or an unsynced cloud file leaves
    // behind. Every upload path funnels through here, so it is stopped once.
    if (!file?.size) {
      enqueueSnackbar(
        'This file is empty. Download your resume again, then upload it.',
      );
      return rejectWithValue({ error: 'empty_resume_file' });
    }
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadResume({ resumeFile: file, setAsDefault, fastParse });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const changeResumeSetting = createAsyncThunk(
  '/change-resume-setting',
  async (
    {
      resumeId,
      isDefault,
      title,
      visibility,
    }: {
      resumeId: string;
      isDefault?: boolean;
      title?: string;
      visibility?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateUserResumeVisibility({
        resumeId,
        isDefault,
        title,
        visibility,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAllUploadedResume = createAsyncThunk(
  'get/all-uploaded-resume',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiFetchUserResumesJson();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteUserResume = createAsyncThunk(
  'delete/user-resume',
  async ({ resumeId }: { resumeId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteUserResume({ resumeId });
      enqueueSnackbar('Resume deleted successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errKey = err?.response?.data?.error;
      if (errKey === 'default_resume_in_active_application') {
        enqueueSnackbar(
          'You cannot delete your default resume while you have an application on an active job. Set another resume as default first.',
          {
            variant: 'warning',
            SnackbarProps: { style: { zIndex: 1600 } },
            style: { zIndex: 1600 },
          },
        );
      } else {
        enqueueSnackbar(getFormattedErr(errKey), {
          SnackbarProps: { style: { zIndex: 1600 } },
          style: { zIndex: 1600 },
        });
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getGeneratedResumeData = createAsyncThunk(
  'get/generated-resume-data',
  async ({ resumeId }: { resumeId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiFetchResumeJson({ resumeId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateDataFromResume = createAsyncThunk(
  'post/generate-data-from-resume',
  async ({ url }: { url: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGenerateResume({
        jsonResumeGenerate: { url },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAbekusProfile = createAsyncThunk(
  'user/public_profile',
  async (
    {
      username,
      onlyAnalytics,
      onlyAttempted = true,
      page = 1,
      pageSize = 10,
    }: {
      username: string;
      onlyAnalytics?: boolean;
      onlyAttempted?: boolean;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserPublicProfile(
        {
          username,
          onlyAnalytics,
          onlyAttempted,
          pageNo: page,
          pageSize,
        },
        {
          signal,
        },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchMonthlyScore = createAsyncThunk(
  'user/monthly-score',
  async (
    {
      username,
      endMonth,
      months,
      skillId,
    }: {
      username: string;
      endMonth?: string;
      months?: number;
      skillId?: string;
      type: 'profile' | 'review' | 'spotlight';
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserMonthlyScore({ username, endMonth, months, skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchSkillBuilderUserActivity = createAsyncThunk(
  'username/getUserActivity',
  async (
    {
      username,
      selectedMonth,
      selectedYear,
      summaryType,
    }: {
      username: string;
      selectedMonth?: string;
      selectedYear?: string;
      summaryType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getScoreApi()
      ).scoreApiGetUserDailyActivity({
        username,
        selectedMonth,
        selectedYear,
        summaryType,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchContributeUserActivity = createAsyncThunk(
  'get/contribute-user-activity',
  async (
    {
      username,
      selectedMonth,
      selectedYear,
      summaryType,
    }: {
      username: string;
      selectedMonth?: string;
      selectedYear?: string;
      summaryType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getScoreApi()
      ).scoreApiGetUserContributionActivity({
        username,
        selectedMonth,
        selectedYear,
        summaryType,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchUserCertificates = createAsyncThunk(
  'user/certificates',
  async ({ username }: { username: string }, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserCertificates({ username }, { signal });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);

export const setCertificateVisibility = createAsyncThunk(
  'user/certificates/visibility',
  async (
    { certificateId, isPublic }: { certificateId: string; isPublic: boolean },
    { rejectWithValue },
  ) => {
    try {
      await (
        await apiClient.getUserApi()
      ).userApiSetCertificateVisibility({
        certificateId,
        certificateVisibilityBody: { is_public: isPublic },
      });
      return { certificateId, isPublic };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const fetchActivityCalendar = createAsyncThunk(
  'user/activity-calendar',
  async (
    {
      username,
      startDate,
      endDate,
    }: { username: string; startDate: string; endDate: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserActivityCalendar(
        { username, startDate, endDate },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);

export const fetchSkillReport = createAsyncThunk(
  'skill/report',
  async (
    { skillId, username }: { skillId: string; username?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillReport({ skillId, username }, { signal });
      return response.data;
    } catch (err: any) {
      // Supplementary spotlight detail behind auth — a logged-out visitor
      // legitimately 401s here, so degrade quietly to the base skill stats.
      return rejectWithValue(err.response?.data);
    }
  },
);
